import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { employmentLabel, formatSalary } from "@/lib/jobs";
import { CheckCircle2, Clock, Star, XCircle, Briefcase, Sparkles, MessageCircle, ExternalLink } from "lucide-react";
import ApplicationChat from "@/components/ApplicationChat";

const STATUS_META = {
  submitted: { icon: Clock,        cls: "bg-[#F2F2F0] text-[#4A4A4A]",  label: "Submitted" },
  reviewed:  { icon: Sparkles,     cls: "bg-[#F2F2F0] text-[#4A4A4A]",  label: "Reviewed" },
  shortlisted: { icon: Star,       cls: "bg-[#EEF3FF] text-[#0057FF]",  label: "Shortlisted" },
  hired:     { icon: CheckCircle2, cls: "bg-[#0A0A0A] text-white",       label: "Hired" },
  rejected:  { icon: XCircle,      cls: "bg-[#FBE8E5] text-[#B84B4B]",   label: "Not selected" },
};

export default function JobApplications() {
  const { user } = useAuth();
  const [apps, setApps] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/jobs/applications/sent")
      .then(r => setApps(r.data || []))
      .catch(e => setErr(formatErr(e)));
  }, []);

  if (!user) return <div className="max-w-md mx-auto p-10"><p>Please sign in.</p></div>;

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 py-10" data-testid="job-applications-page">
      <p className="overline">Your career · Hoomanz</p>
      <h1 className="h-display text-4xl">Applications you&apos;ve sent.</h1>
      <p className="text-[#4A4A4A] mt-2">Track where you stand. Chat with recruiters right inside each application.</p>
      {err && <p className="text-sm text-[#B84B4B] mt-3">{err}</p>}

      <div className="space-y-3 mt-7" data-testid="applications-list">
        {apps === null && <p className="text-[#8A8A8A]">Loading…</p>}
        {apps && apps.length === 0 && (
          <div className="card p-8 text-center">
            <Briefcase size={26} className="mx-auto text-[#0057FF] mb-2"/>
            <p className="text-[#4A4A4A]">You haven&apos;t applied to any jobs yet.</p>
            <Link to="/jobs" className="btn-secondary inline-flex mt-3">Browse jobs</Link>
          </div>
        )}
        {(apps || []).map(a => <ApplicationRow key={a.id} a={a} />)}
      </div>
    </div>
  );
}

function ApplicationRow({ a }) {
  const [chatOpen, setChatOpen] = useState(false);
  const meta = STATUS_META[a.status] || STATUS_META.submitted;
  const Icon = meta.icon;
  const j = a.job || {};
  return (
    <div className="card p-5" data-testid={`application-row-${a.id}`}>
      <div className="flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <p className="font-medium">{j.title || "Job removed"}</p>
          <p className="text-sm text-[#4A4A4A] mt-0.5">
            {j.company_name || j.employer_id} · {employmentLabel(j.employment_type)} · {j.location || "Remote"}
          </p>
          <p className="text-xs text-[#8A8A8A] mt-1">Applied {new Date(a.created_at).toLocaleDateString()}</p>
        </div>
        <div className="text-right">
          <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium ${meta.cls}`}
                data-testid={`application-status-${a.id}`}>
            <Icon size={11}/> {meta.label}
          </span>
          <p className="text-xs text-[#8A8A8A] mt-2">{formatSalary(j)}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-3">
        <button onClick={()=>setChatOpen(o=>!o)}
                className={`text-xs ${chatOpen ? "btn-primary !py-1.5 !px-3" : "btn-ghost"}`}
                data-testid={`application-chat-toggle-${a.id}`}>
          <MessageCircle size={12}/> {chatOpen ? "Close chat" : "Message recruiter"}
        </button>
        {a.job_id && (
          <Link to={`/jobs/${a.job_id}`} className="btn-ghost text-xs" data-testid={`application-view-${a.id}`}>
            <ExternalLink size={12}/> View job
          </Link>
        )}
      </div>
      {chatOpen && (
        <div className="mt-3" data-testid={`application-chat-${a.id}`}>
          <ApplicationChat appId={a.id} compact />
        </div>
      )}
    </div>
  );
}
